import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../../ui/table";

import Badge from "../../ui/badge/Badge";
import { formatCurrencyValue } from "../../../utils/formatters";
import { Edit, ExternalLink, Trash2, Copy, Check, AlertCircle } from 'lucide-react';
import MoreButton from '../../common/MoreButton';
import { useNavigate, useParams } from "react-router";
import api from "../../../services/api";
import authService from "../../../services/authService";
import Pagination from "../../ui/pagination/Pagination";
import ComponentCard from "../../common/ComponentCard";
import Button from "../../ui/button/Button";
import Modal from '../../common/Modal';
import Alert from '../../ui/alert/Alert';

// Definição da interface da oferta
interface Offer {
    id: string;
    offerId: string;
    productId: string;
    name: string;
    slug: string;
    customUrl: string | null;
    price: string;
    currency: string;
    active: boolean;
    isDefault: boolean;
    type: string;
    createdAt: string;
    updatedAt: string;
    paymentLink?: PaymentLink[];
    status: string;
}

interface PaymentLink {
    id: string;
    paymentLinkId: string;
    offerId: string;
    code: string;
    fullUrl: string;
    name: string | null;
    status: string;
    type: string;
    createdAt: string;
    updatedAt: string;
}

interface ProductOfferTableProps {
    productId?: string;
}

const currencySymbols: Record<string, string> = {
    BRL: 'R$',
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    // Adicione outras moedas que usar...
};

export default function ProductOfferTable({ productId }: ProductOfferTableProps) {
    const navigate = useNavigate();
    const { id: paramId } = useParams<{ id: string }>();
    const productIdToUse = productId || paramId;
    const [offers, setOffers] = useState<Offer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [alertInfo, setAlertInfo] = useState<{
        show: boolean;
        type: "success" | "error" | "warning" | "info";
        message: string;
        title: string;
    } | null>(null);

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [offerToDelete, setOfferToDelete] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

    useEffect(() => {
        const fetchProductOffers = async () => {
            try {
                setLoading(true);
                setError(null);

                // Usando um novo endpoint específico para buscar ofertas com links de pagamento
                const response = await api.get(`/api/products/${productIdToUse}/offers-with-links`, {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.data && response.data.offers) {
                    // Filtrar ofertas para remover aquelas com status "deleted"
                    const filteredOffers = response.data.offers.filter((offer: Offer) =>
                        offer.status !== "deleted"
                    );
                    setOffers(filteredOffers);
                } else {
                    setOffers([]);
                }
            } catch (err: any) {
                console.error("Erro ao buscar ofertas do produto:", err);
                setError(err.message || "Erro ao carregar ofertas do produto");
            } finally {
                setLoading(false);
            }
        };

        if (productIdToUse) {
            fetchProductOffers();
        }
    }, [productIdToUse]);

    // Função para editar uma oferta
    const handleEditOffer = (offerId: string) => {
        navigate(`/products/edit/${productIdToUse}/offer/${offerId}`);
    };

    // Função para obter o link de venda
    const generateSalesLink = (offer: Offer) => {
        // Verificar se a oferta tem links de pagamento
        if (offer.paymentLink && offer.paymentLink.length > 0) {
            // Encontrar o primeiro link que tenha fullUrl
            const paymentLink = offer.paymentLink.find(link => link.fullUrl);
            if (paymentLink && paymentLink.fullUrl) {
                return paymentLink.fullUrl;
            }
        }

        // Fallback para customUrl se existir
        if (offer.customUrl) {
            return offer.customUrl;
        }

        // Retornar uma mensagem se não houver link disponível
        return "#";
    };

    // Função para mudar de página
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Cálculo para paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = offers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(offers.length / itemsPerPage);

    // Componente para exibir e copiar o link
    const CopyLinkInput = ({ url }: { url: string }) => {
        const [copied, setCopied] = useState(false);

        const handleCopy = () => {
            navigator.clipboard.writeText(url)
                .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                })
                .catch(err => {
                    console.error('Erro ao copiar: ', err);
                });
        };

        return (
            <div className="relative flex items-center w-full">
                <input
                    type="text"
                    value={url}
                    readOnly
                    onClick={handleCopy}
                    className="w-full py-2 px-3 pr-12 border border-gray-300 rounded text-sm text-gray-700 bg-gray-50 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                />
                <div
                    onClick={handleCopy}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-800/40 rounded-md cursor-pointer transition-colors"
                    title="Copiar link"
                >
                    {copied ? <Check size={18} className="text-green-600 dark:text-green-400" /> : <Copy size={18} className="text-blue-600 dark:text-blue-400" />}
                </div>
            </div>
        );
    };

    const handleDeleteOffer = async () => {
        if (!offerToDelete) return;

        setDeleteLoading(true);
        try {
            await api.delete(
                `/api/products/${productIdToUse}/offers/${offerToDelete}`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Fechar o modal e limpar o estado
            setDeleteModalOpen(false);
            setOfferToDelete(null);
            setDeleteLoading(false);

            // Recarregar os dados
            const fetchProductOffers = async () => {
                try {
                    setLoading(true);
                    setError(null);

                    // Usando um novo endpoint específico para buscar ofertas com links de pagamento
                    const response = await api.get(`/api/products/${productIdToUse}/offers-with-links`, {
                        headers: {
                            'Authorization': `Bearer ${authService.getToken()}`
                        }
                    });

                    if (response.data && response.data.offers) {
                        // Filtrar ofertas para remover aquelas com status "deleted"
                        const filteredOffers = response.data.offers.filter((offer: Offer) =>
                            offer.status !== "deleted"
                        );
                        setOffers(filteredOffers);
                    } else {
                        setOffers([]);
                    }
                } catch (err: any) {
                    console.error("Erro ao buscar ofertas do produto:", err);
                    setError(err.message || "Erro ao carregar ofertas do produto");
                } finally {
                    setLoading(false);
                }
            };
            fetchProductOffers();

            // Mostrar mensagem de sucesso
            setAlertInfo({
                show: true,
                type: "success",
                title: "Sucesso!",
                message: "Oferta excluída com sucesso."
            });

            // Esconder a mensagem após 3 segundos
            setTimeout(() => setAlertInfo(null), 3000);
        } catch (error: any) {
            console.error("Erro ao excluir oferta:", error);
            
            // Extrair a mensagem de erro da resposta da API
            let errorMessage = "Erro ao excluir oferta. Tente novamente.";
            
            // Verificar se é um erro do Axios e se tem uma resposta com dados
            if (error && error.response && error.response.data) {
                // Se houver uma mensagem específica na resposta da API, use-a
                errorMessage = error.response.data.message || errorMessage;
            }
            
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: errorMessage
            });
            setDeleteLoading(false);
            // Fechar o modal quando ocorrer um erro
            setDeleteModalOpen(false);
        }
    };

    const openDeleteModal = (offerId: string) => {
        setOfferToDelete(offerId);
        setDeleteModalOpen(true);
    };

    if (loading) {
        return <div className="p-4 text-center">Carregando ofertas...</div>;
    }

    if (error) {
        return <div className="p-4 text-center text-red-500">Erro: {error}</div>;
    }

    if (offers.length === 0) {
        return (
            <ComponentCard title="Ofertas">
                <div className="p-8 flex flex-col items-center gap-6 text-gray-500">
                    Este produto ainda não possui ofertas.
                    <Button
                        size="md"
                        variant="primary"
                        disabled={loading}
                        onClick={() => navigate(`/products/${productId}/new-offer`)}
                    >
                        Adicionar Oferta
                    </Button>
                </div>
            </ComponentCard>
        );
    }

    return (
        <ComponentCard title="Todas as Ofertas">
            <div className="col-span-12 space-y-6">
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        {/* Campo de busca - com largura limitada */}
                        <div className="relative w-full md:w-1/3 lg:w-1/4">
                            <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                <svg
                                    className="fill-gray-500 dark:fill-gray-400"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                        fill=""
                                    />
                                </svg>
                            </button>
                            <input
                                type="text"
                                placeholder="Pesquisar oferta..."
                                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                            />
                        </div>

                        {/* Botão - diretamente ao lado do campo de busca */}
                        <Button
                            size="md"
                            variant="primary"
                            disabled={loading}
                            onClick={() => navigate(`/products/${productId}/new-offer`)}
                        >
                            Adicionar Oferta
                        </Button>
                    </div>
                </div>
            </div>
            <div className="overflow-hidden rounded-xl">
                <div className="max-w-full overflow-x-auto">
                    <Table>
                        {/* Table Header */}
                        <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                            <TableRow>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Código
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Nome
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Preço
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Link
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Status
                                </TableCell>

                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Acessar
                                </TableCell>

                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 pr-8"
                                >
                                    Ações
                                </TableCell>
                            </TableRow>
                        </TableHeader>

                        {/* Table Body */}
                        <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                            {currentItems.map((offer) => (
                                <TableRow key={offer.id}>
                                    <TableCell className="px-5 py-4 text-start">
                                        <span className="block text-gray-500 text-theme-xs dark:text-gray-400">
                                            {offer.offerId.toUpperCase()}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div>
                                            <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                {offer.name}
                                                {offer.isDefault && (
                                                    <span className="inline-block ms-3 mt-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full dark:bg-blue-900/30 dark:text-blue-400">
                                                        Padrão
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {currencySymbols[offer.currency]} {formatCurrencyValue(parseFloat(offer.price), offer.currency)}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {offer.paymentLink && offer.paymentLink.length > 0 ? (
                                            <div className="flex flex-col gap-1">
                                                {offer.paymentLink.map((link) => (
                                                    <CopyLinkInput key={link.id} url={link.fullUrl} />
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">Link não disponível</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <Badge
                                            size="sm"
                                            color={offer.active ? "success" : "error"}
                                        >
                                            {offer.active ? "Ativo" : "Inativo"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {offer.paymentLink && offer.paymentLink.length > 0 ? (
                                            <a
                                                href={generateSalesLink(offer)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1 text-blue-500 hover:text-blue-700"
                                            >
                                                Visualizar <ExternalLink size={14} />
                                            </a>
                                        ) : (
                                            <span className="text-gray-400">Não disponível</span>
                                        )}
                                    </TableCell>

                                    <TableCell className="px-4 py-3 text-gray-500 text-end text-theme-sm dark:text-gray-400 pr-8">
                                        <div className="flex justify-end gap-2 relative">
                                            <MoreButton
                                                options={[
                                                    {
                                                        label: "Editar",
                                                        onClick: () => handleEditOffer(offer.id),
                                                        icon: <Edit className="size-4" />
                                                    },
                                                    {
                                                        label: "Excluir",
                                                        onClick: () => openDeleteModal(offer.id),
                                                        icon: <Trash2 className="size-4" />,
                                                        className: "text-red-600 dark:text-red-400"
                                                    }
                                                ]}
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {totalPages > 1 && (
                        <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {alertInfo && alertInfo.show && (
                <div className="mt-4">
                    <Alert
                        variant={alertInfo.type}
                        title={alertInfo.title}
                        message={alertInfo.message}
                    />
                </div>
            )}
            {/* Modal de confirmação de exclusão */}
            <Modal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                size="sm"
                onConfirm={handleDeleteOffer}
                confirmText="Excluir"
                cancelText="Cancelar"
                confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
                confirmLoading={deleteLoading}
            >
                <div className="flex flex-col items-center text-center py-4">
                    <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tem certeza que deseja excluir esta oferta?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        Não será possível reverter esta ação!
                    </p>
                </div>
            </Modal>
        </ComponentCard>
    );
}